/**
 * VIDÉOS TECHNIQUES — Admin /app/videos
 * CRUD capsules : multi-plateformes, niveau d'accès (public/membres/premium),
 * gamification (crédits + badge). Thème admin sombre (zinc).
 */
import { useEffect, useState, useCallback, useRef } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import * as tus from "tus-js-client";
import { Plus, PencilSimple, Trash, CircleNotch, VideoCamera, Lock, Globe, Users, Star, CloudArrowUp, CheckCircle, WarningCircle, LinkSimple, MagnifyingGlass, Play } from "@phosphor-icons/react";

const RED = "#D90429";

// Convertit une URL vidéo en source lisible pour l'aperçu (iframe Bunny/YouTube ou balise <video>).
function _previewSrc(url) {
  if (!url) return null;
  if (/mediadelivery\.net\/embed\//i.test(url)) return url; // Bunny embed
  if (/youtube\.com|youtu\.be/i.test(url)) {
    const m = url.match(/(?:v=|youtu\.be\/|embed\/)([\w-]{6,})/);
    return m ? `https://www.youtube.com/embed/${m[1]}` : url;
  }
  if (/vimeo\.com\/(\d+)/i.test(url)) {
    const m = url.match(/vimeo\.com\/(\d+)/i);
    return m ? `https://player.vimeo.com/video/${m[1]}` : url;
  }
  return url;
}
function _isFileVideo(url) { return /\.(mp4|webm|mov|m4v|ogg)(\?|$)/i.test(url || ""); }

// Aperçu vidéo en modale — vérifier le bon jumelage d'une technique/capsule.
function VideoPreviewModal({ url, title, onClose }) {
  const src = _previewSrc(url);
  return (
    <div className="fixed inset-0 z-[90] bg-black/80 flex items-center justify-center p-4" onClick={onClose} data-testid="video-preview-modal">
      <div className="bg-zinc-950 border border-zinc-700 rounded-xl w-full max-w-3xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800">
          <div className="text-sm font-semibold text-zinc-100 truncate flex items-center gap-2"><Play size={16} weight="fill" className="text-emerald-400" /> Aperçu — <span className="text-zinc-400 font-normal truncate">{title || "vidéo"}</span></div>
          <button onClick={onClose} data-testid="video-preview-close" className="text-zinc-400 hover:text-white text-xl">×</button>
        </div>
        <div className="p-4">
          {!src ? (
            <div className="text-center py-16 text-zinc-500" data-testid="video-preview-empty">Aucune vidéo reliée à afficher.</div>
          ) : _isFileVideo(src) ? (
            <video src={src} controls autoPlay className="w-full rounded-lg bg-black aspect-video" data-testid="video-preview-player" />
          ) : (
            <div className="relative w-full rounded-lg overflow-hidden bg-black" style={{ paddingTop: "56.25%" }}>
              <iframe src={src} title={title || "aperçu"} allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture" allowFullScreen loading="lazy" className="absolute inset-0 w-full h-full" data-testid="video-preview-player" />
            </div>
          )}
          {url && <a href={_previewSrc(url)} target="_blank" rel="noreferrer" className="block mt-3 text-xs text-sky-400 hover:underline truncate" data-testid="video-preview-open-tab">Ouvrir dans un nouvel onglet ↗</a>}
        </div>
      </div>
    </div>
  );
}
const ACCESS_BADGE = {
  public: "bg-emerald-900/60 text-emerald-200 border-emerald-700",
  membres: "bg-blue-900/60 text-blue-200 border-blue-700",
  premium: "bg-amber-900/60 text-amber-200 border-amber-700",
};
const ACCESS_ICON = { public: Globe, membres: Users, premium: Star };

export default function VideoTechAdminModule() {
  const [videos, setVideos] = useState([]);
  const [refs, setRefs] = useState(null);
  const [byAccess, setByAccess] = useState({});
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [premium, setPremium] = useState(null);
  const [bunnyCfg, setBunnyCfg] = useState(null);
  const [attachVideo, setAttachVideo] = useState(null);
  const [previewVideo, setPreviewVideo] = useState(null);
  const [search, setSearch] = useState("");
  const [voletFilter, setVoletFilter] = useState("all"); // all | technique | combo | training
  const [accessFilter, setAccessFilter] = useState("all"); // all | public | membres | premium
  const [sortBy, setSortBy] = useState("default"); // default | title | discipline | popular
  const [aiMatchOpen, setAiMatchOpen] = useState(false);
  const [shootingOpen, setShootingOpen] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [dashOpen, setDashOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState("");
  const [coverage, setCoverage] = useState(null);
  const [classifying, setClassifying] = useState(false);

  const loadCoverage = useCallback(async () => {
    try { const { data } = await api.get("/curriculum/video-coverage"); setCoverage(data); } catch {}
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      await api.post("/videos/bunny/sync-pending").catch(() => {});
      api.post("/videos/bunny/refresh-views").catch(() => {});
      const [v, r, p] = await Promise.all([api.get("/videos/admin"), api.get("/videos/admin/refs"), api.get("/videos/admin/premium-stats")]);
      setVideos(v.data.videos); setByAccess(v.data.by_access); setRefs(r.data); setPremium(p.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadCoverage(); }, [loadCoverage]);
  useEffect(() => { api.get("/videos/bunny/config").then((r) => setBunnyCfg(r.data)).catch(() => {}); }, []);

  async function seed() {
    try { const r = await api.post("/videos/admin/seed"); toast.success(r.data.skipped ? "Déjà présent" : `${r.data.seeded} capsules ajoutées`); load(); }
    catch { toast.error("Erreur"); }
  }
  async function remove(id) {
    if (!window.confirm("Supprimer cette capsule ?")) return;
    await api.delete(`/videos/admin/${id}`); toast.success("Supprimée"); load();
  }

  async function purgeYoutube() {
    if (!window.confirm("Effacer DÉFINITIVEMENT tout le contenu YouTube restant (fiches bibliothèque, liens vidéo des techniques et programmes) ? Les vidéos Bunny ne sont PAS touchées.")) return;
    try {
      const r = await api.post("/videos/admin/purge-youtube");
      toast.success(`YouTube effacé — ${r.data.library_deleted} capsule(s), ${r.data.techniques_cleared} technique(s), ${r.data.programs_cleared} programme(s)`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  }

  async function reconcileBunny() {
    if (!window.confirm("Corriger les vidéos 404 : comparer aux vidéos RÉELLES de Bunny et retirer les liens qui n'existent pas (cause des erreurs 404). Continuer ?")) return;
    try {
      const r = await api.post("/videos/bunny/reconcile");
      toast.success(`Corrigé — ${r.data.real_videos} vidéo(s) réelle(s) · ${r.data.techniques_cleared} technique(s) nettoyée(s) · ${r.data.library_removed} fiche(s) orpheline(s) retirée(s)`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur de réconciliation Bunny"); }
  }

  async function importBunnyLibrary() {
    if (!window.confirm("Importer toutes les vidéos de ta bibliothèque Bunny dans l'app ? Les vidéos déjà présentes seront mises à jour (miniature/durée), sans perdre leur classement.")) return;
    try {
      const r = await api.post("/videos/bunny/import-library");
      toast.success(`Bunny : ${r.data.imported} importée(s), ${r.data.updated} mise(s) à jour (${r.data.total_bunny} au total)`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur d'import Bunny"); }
  }

  async function classifyVideos() {
    if (!window.confirm("Classer automatiquement les vidéos sans discipline (par IA) selon leur titre ? Discipline + niveau d'accès seront proposés.")) return;
    setClassifying(true);
    try {
      const r = await api.post("/videos/admin/ai-classify", {});
      toast.success(`${r.data.classified} vidéo(s) classée(s) par l'IA`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur de classement IA"); }
    finally { setClassifying(false); }
  }

  async function syncAll() {
    if (!window.confirm("Tout synchroniser : Importer depuis Bunny → Classer (IA) → Jumeler aux carnets. Les liens verrouillés 🔒 ne seront pas touchés. Continuer ?")) return;
    setSyncing(true); setSyncMsg("Démarrage…");
    try {
      const { data } = await api.post("/curriculum/sync-all/start");
      const jobId = data.job_id;
      const started = Date.now();
      const stages = { start: "Démarrage…", import: "Import Bunny…", classify: "Classement IA…", match: "Jumelage aux carnets…", done: "Terminé" };
      const poll = async () => {
        try {
          const { data: job } = await api.get(`/curriculum/ai-match-videos/job/${jobId}`);
          if (job.status === "done") {
            toast.success(`Synchro terminée : ${job.imported || 0} importée(s), ${job.classified || 0} classée(s), ${job.applied || 0} reliée(s)`);
            setSyncing(false); setSyncMsg(""); load(); loadCoverage(); return;
          }
          if (job.status === "error") { toast.error("Erreur de synchronisation"); setSyncing(false); setSyncMsg(""); return; }
          let m = stages[job.stage] || "En cours…";
          if (job.stage === "match" && job.total_disc) m = `Jumelage… ${job.done_disc}/${job.total_disc} discipline(s)`;
          setSyncMsg(m);
          if (Date.now() - started > 360000) { toast.error("La synchro prend trop de temps."); setSyncing(false); setSyncMsg(""); return; }
          setTimeout(poll, 3000);
        } catch { setTimeout(poll, 3000); }
      };
      setTimeout(poll, 2500);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur de synchro"); setSyncing(false); setSyncMsg(""); }
  }

  if (loading) return <div className="flex justify-center py-32"><CircleNotch size={32} className="animate-spin text-zinc-400" /></div>;

  return (
    <div className="max-w-6xl mx-auto p-6" data-testid="video-admin">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-2">
        <div className="flex items-center gap-3">
          <VideoCamera size={26} weight="fill" className="text-zinc-200" />
          <h1 className="text-2xl font-bold">Vidéos techniques</h1>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={importBunnyLibrary} data-testid="import-bunny-btn" className="border border-sky-700 text-sky-300 px-4 py-2 rounded text-sm font-semibold hover:bg-sky-950/40">🐰 Importer depuis Bunny</button>
          <button onClick={reconcileBunny} data-testid="reconcile-bunny-btn" className="border border-red-700 text-red-300 px-4 py-2 rounded text-sm font-semibold hover:bg-red-950/40">🔧 Corriger les 404 (Bunny)</button>
          <button onClick={syncAll} disabled={syncing} data-testid="sync-all-btn" className="border border-amber-600 text-amber-300 px-4 py-2 rounded text-sm font-semibold hover:bg-amber-950/40 disabled:opacity-60">{syncing ? `🔄 ${syncMsg || "Synchro…"}` : "🔄 Tout synchroniser"}</button>
          <button onClick={classifyVideos} disabled={classifying} data-testid="classify-videos-btn" className="border border-violet-700 text-violet-300 px-4 py-2 rounded text-sm font-semibold hover:bg-violet-950/40 disabled:opacity-50">{classifying ? "Classement…" : "🏷️ Classer (IA)"}</button>
          <button onClick={() => setManualOpen(true)} data-testid="manual-link-btn" className="border border-zinc-600 text-zinc-200 px-4 py-2 rounded text-sm font-semibold hover:bg-zinc-800">🔗 Liens manuels</button>
          <button onClick={() => setAiMatchOpen(true)} data-testid="ai-match-btn" className="border border-emerald-700 text-emerald-300 px-4 py-2 rounded text-sm font-semibold hover:bg-emerald-950/40">🤖 Jumeler aux carnets (IA)</button>
          <button onClick={purgeYoutube} data-testid="purge-youtube-btn" className="border border-amber-800 text-amber-300 px-4 py-2 rounded text-sm font-semibold hover:bg-amber-950/40">🧹 Purger YouTube</button>
          <button onClick={() => setEditing("new")} data-testid="add-video-btn" className="flex items-center gap-2 text-white px-5 py-2.5 font-semibold rounded-md" style={{ background: RED }}><Plus size={18} weight="bold" /> Ajouter une capsule</button>
        </div>
      </div>
      {aiMatchOpen && <AiMatchModal onClose={() => { setAiMatchOpen(false); loadCoverage(); }} />}
      {shootingOpen && <ShootingListModal onClose={() => { setShootingOpen(false); loadCoverage(); }} />}
      {manualOpen && <ManualLinkModal onClose={() => { setManualOpen(false); loadCoverage(); }} />}
      {dashOpen && <TournageDashboardModal coverage={coverage} onClose={() => setDashOpen(false)} />}
      <p className="text-sm text-zinc-500 mb-5">Public : <a href="/videos" target="_blank" rel="noreferrer" className="underline">/videos</a> · Gamification : crédits + badges décernés à la complétion.</p>

      {coverage && (
        <button onClick={() => setShootingOpen(true)} data-testid="carnet-coverage" className="w-full text-left mb-5 rounded-lg border border-zinc-800 bg-zinc-950/60 p-4 hover:border-zinc-600 transition-colors">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-semibold text-zinc-200">📓 Couverture vidéo du carnet <span className="text-xs font-normal text-zinc-500">(cliquer → liste de tournage)</span></div>
            <div className="text-sm text-zinc-400"><span className="text-emerald-400 font-bold" data-testid="coverage-with-video">{coverage.with_video}</span> / {coverage.total} techniques ont une vidéo <span className="text-zinc-500">· reste {coverage.without_video} à filmer</span></div>
          </div>
          <div className="w-full h-2.5 rounded-full bg-zinc-800 overflow-hidden">
            <div className="h-full bg-emerald-500 transition-all" style={{ width: `${coverage.pct}%` }} data-testid="coverage-bar" />
          </div>
          <div className="flex items-center justify-between text-xs mt-1">
            <span className="text-zinc-500" data-testid="coverage-source">🔒 {coverage.manual_count || 0} manuel(s) · 🤖 {coverage.ai_count || 0} par IA</span>
            <span className="text-zinc-500">{coverage.pct}% · <span onClick={(e) => { e.stopPropagation(); setDashOpen(true); }} className="text-sky-400 hover:underline" data-testid="open-tournage-dash">📊 Tableau de bord</span></span>
          </div>
        </button>
      )}

      <BunnyUploader cfg={bunnyCfg} refs={refs} onDone={load} />
      <ImportProgress count={videos.filter((v) => v.platform === "bunny").length} />

      <div className="grid grid-cols-3 gap-3 mb-6">
        <KPI label="Public" count={byAccess.public || 0} icon={Globe} color="emerald" testid="kpi-public" />
        <KPI label="Membres" count={byAccess.membres || 0} icon={Users} color="blue" testid="kpi-membres" />
        <KPI label="Premium" count={byAccess.premium || 0} icon={Star} color="amber" testid="kpi-premium" />
      </div>

      {/* Rentabilité Premium (MRR) */}
      {premium && (
        <div className="bg-gradient-to-r from-amber-950/40 to-zinc-900 border border-amber-900/50 rounded-lg p-4 mb-6" data-testid="premium-mrr">
          <div className="flex items-center gap-2 text-amber-300 text-xs uppercase tracking-wider mb-3"><Star size={14} weight="fill" /> Rentabilité abonnement Premium</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <MRRStat label="Abonnés premium" value={premium.premium_members} testid="mrr-members" />
            <MRRStat label="MRR (revenu mensuel)" value={`${premium.mrr_cad.toLocaleString("fr-CA", { minimumFractionDigits: 2 })} $`} accent testid="mrr-monthly" />
            <MRRStat label="ARR (revenu annuel)" value={`${premium.arr_cad.toLocaleString("fr-CA", { minimumFractionDigits: 2 })} $`} testid="mrr-annual" />
            <MRRStat label={`Prix (/${premium.billing_cycle})`} value={`${premium.price_cad} $`} testid="mrr-price" />
          </div>
          {premium.pending_subscriptions > 0 && <p className="text-xs text-zinc-500 mt-2">{premium.pending_subscriptions} paiement(s) en attente de confirmation.</p>}
        </div>
      )}

      <div className="mb-3 relative" data-testid="video-search">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="video-search-input"
          placeholder="Rechercher une technique (titre, discipline, ceinture, tag)…"
          className="w-full bg-zinc-900 border border-zinc-800 rounded-lg pl-9 pr-9 py-2.5 text-sm text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:border-zinc-600"
        />
        {search && (
          <button onClick={() => setSearch("")} data-testid="video-search-clear" className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white">✕</button>
        )}
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-4" data-testid="video-filters">
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] uppercase tracking-widest text-zinc-600 mr-1">Volet</span>
          {[["all", "Tous"], ["technique", "Technique"], ["combo", "Combinaison"], ["training", "Training"]].map(([k, l]) => (
            <button key={k} onClick={() => setVoletFilter(k)} data-testid={`filter-volet-${k}`}
              className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${voletFilter === k ? "border-amber-500 bg-amber-500/15 text-amber-200" : "border-zinc-800 text-zinc-500 hover:border-zinc-600"}`}>{l}</button>
          ))}
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] uppercase tracking-widest text-zinc-600 mr-1">Accès</span>
          {[["all", "Tous"], ["public", "Public"], ["membres", "Membres"], ["premium", "Premium"]].map(([k, l]) => (
            <button key={k} onClick={() => setAccessFilter(k)} data-testid={`filter-access-${k}`}
              className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${accessFilter === k ? "border-sky-500 bg-sky-500/15 text-sky-200" : "border-zinc-800 text-zinc-500 hover:border-zinc-600"}`}>{l}</button>
          ))}
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] uppercase tracking-widest text-zinc-600 mr-1">Trier</span>
          {[["default", "Défaut"], ["title", "Titre"], ["discipline", "Discipline"], ["popular", "Popularité"]].map(([k, l]) => (
            <button key={k} onClick={() => setSortBy(k)} data-testid={`sort-${k}`}
              className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${sortBy === k ? "border-emerald-500 bg-emerald-500/15 text-emerald-200" : "border-zinc-800 text-zinc-500 hover:border-zinc-600"}`}>{l}</button>
          ))}
        </div>
      </div>

      <div className="space-y-2" data-testid="videos-list">
        {(() => {
          const q = search.trim().toLowerCase();
          let filtered = videos.filter((v) => {
            if (q && ![v.title, v.discipline, v.belt_level, v.volet, ...(v.tags || [])].filter(Boolean).join(" ").toLowerCase().includes(q)) return false;
            if (voletFilter !== "all" && (v.volet || "technique") !== voletFilter) return false;
            if (accessFilter !== "all" && v.access_level !== accessFilter) return false;
            return true;
          });
          if (sortBy === "title") filtered = [...filtered].sort((a, b) => (a.title || "").localeCompare(b.title || "", "fr"));
          else if (sortBy === "discipline") filtered = [...filtered].sort((a, b) => (a.discipline || "").localeCompare(b.discipline || "", "fr"));
          else if (sortBy === "popular") filtered = [...filtered].sort((a, b) => (b.views || 0) - (a.views || 0));
          return (<>
        {filtered.map((v) => {
          const AccIcon = ACCESS_ICON[v.access_level] || Globe;
          return (
            <div key={v.id} className="border border-zinc-800 bg-zinc-900 rounded-lg p-3 flex items-center gap-3 flex-wrap" data-testid={`video-${v.id}`}>
              <div className="w-20 h-12 rounded bg-zinc-950 border border-zinc-800 overflow-hidden flex items-center justify-center shrink-0" data-testid={`thumb-${v.id}`}>
                {v.thumbnail_url
                  ? <img src={v.thumbnail_url} alt="" referrerPolicy="no-referrer" onError={(e) => { e.currentTarget.style.display = "none"; }} className="w-full h-full object-cover" />
                  : v.platform === "bunny" && v.bunny_status && v.bunny_status !== "ready"
                    ? <CircleNotch size={16} className="animate-spin text-zinc-500" />
                    : <VideoCamera size={16} className="text-zinc-700" />}
              </div>
              <span className="text-[10px] uppercase font-mono text-zinc-500 w-14">{v.platform}</span>
              <span className="font-semibold flex-1 min-w-[160px]">{v.title}<span className="text-zinc-500 font-normal text-sm"> · {v.discipline || "—"}</span>
                {v.platform === "bunny" && v.bunny_status && v.bunny_status !== "ready" && <span className="ml-2 text-[10px] text-amber-400">⏳ encodage…</span>}
              </span>
              <span className="text-xs text-zinc-500">{v.belt_level || "—"}</span>
              <span className="text-xs text-amber-300 flex items-center gap-1">{v.credits_reward} cr.</span>
              {v.badge && <span className="text-[10px] bg-zinc-800 text-zinc-300 px-1.5 py-0.5 rounded">{v.badge}</span>}
              <span className={`text-[10px] uppercase px-2 py-0.5 rounded border flex items-center gap-1 ${ACCESS_BADGE[v.access_level] || ""}`}><AccIcon size={11} />{v.access_level}</span>
              {!v.published && <span className="text-[10px] uppercase bg-zinc-800 text-zinc-500 px-1.5 py-0.5 rounded">brouillon</span>}
              <button onClick={() => setPreviewVideo({ url: v.embed_url || v.video_url, title: v.title })} data-testid={`preview-${v.id}`} title="Aperçu de la vidéo" className="text-zinc-500 hover:text-emerald-400"><Play size={18} weight="fill" /></button>
              <button onClick={() => setAttachVideo(v)} data-testid={`attach-${v.id}`} title="Rattacher à une technique du carnet" className="text-zinc-500 hover:text-cyan-400"><LinkSimple size={18} /></button>
              <button onClick={() => setEditing(v)} data-testid={`edit-${v.id}`} className="text-zinc-500 hover:text-white"><PencilSimple size={18} /></button>
              <button onClick={() => remove(v.id)} data-testid={`del-${v.id}`} className="text-zinc-500 hover:text-red-500"><Trash size={18} /></button>
            </div>
          );
        })}
        {filtered.length === 0 && videos.length > 0 && <p className="text-zinc-500 py-8 text-center" data-testid="video-search-empty">Aucune technique ne correspond aux filtres{search ? ` (« ${search} »)` : ""}.</p>}
        {videos.length === 0 && <p className="text-zinc-500 py-8 text-center">Aucune capsule. Ajoute-en une ou charge les exemples.</p>}
          </>);
        })()}
      </div>

      {editing && <VideoModal video={editing === "new" ? null : editing} refs={refs} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {attachVideo && <AttachTechniqueModal video={attachVideo} onClose={() => setAttachVideo(null)} />}
      {previewVideo && <VideoPreviewModal url={previewVideo.url} title={previewVideo.title} onClose={() => setPreviewVideo(null)} />}
    </div>
  );
}

function VideoModal({ video, refs, onClose, onSaved }) {
  const [f, setF] = useState({
    title: video?.title || "", description: video?.description || "",
    discipline: video?.discipline || (refs?.disciplines?.[0] || ""), belt_level: video?.belt_level || "",
    platform: video?.platform || "youtube", video_url: video?.video_url || "",
    thumbnail_url: video?.thumbnail_url || "", access_level: video?.access_level || "public",
    credits_reward: video?.credits_reward ?? 10, badge: video?.badge || "",
    tags: (video?.tags || []).join(", "), duration_min: video?.duration_min ?? "",
    published: video?.published ?? true, order: video?.order ?? 0,
    volet: video?.volet || (video?.is_combo ? "combo" : "technique"),
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  async function save() {
    if (!f.title.trim()) return toast.error("Titre requis");
    setBusy(true);
    const payload = {
      ...f, credits_reward: parseInt(f.credits_reward) || 0, order: parseInt(f.order) || 0,
      duration_min: f.duration_min === "" ? null : parseInt(f.duration_min),
      tags: f.tags.split(",").map((x) => x.trim()).filter(Boolean),
    };
    try {
      if (video) await api.put(`/videos/admin/${video.id}`, payload);
      else await api.post("/videos/admin", payload);
      toast.success(video ? "Capsule modifiée" : "Capsule ajoutée"); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    setBusy(false);
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="video-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-zinc-950 border border-zinc-800 rounded-lg w-full max-w-lg p-6 max-h-[92vh] overflow-y-auto">
        <h3 className="text-lg font-bold mb-4">{video ? "Modifier la capsule" : "Nouvelle capsule"}</h3>
        <div className="grid grid-cols-2 gap-3">
          <Fld label="Titre" full><input value={f.title} onChange={(e) => set("title", e.target.value)} data-testid="v-title" className="vinp" /></Fld>
          <Fld label="Plateforme"><select value={f.platform} onChange={(e) => set("platform", e.target.value)} data-testid="v-platform" className="vinp">{refs?.platforms.map((p) => <option key={p}>{p}</option>)}</select></Fld>
          <Fld label="Niveau d'accès"><select value={f.access_level} onChange={(e) => set("access_level", e.target.value)} data-testid="v-access" className="vinp">{refs?.access_levels.map((a) => <option key={a}>{a}</option>)}</select></Fld>
          <Fld label="URL de la vidéo" full><input value={f.video_url} onChange={(e) => set("video_url", e.target.value)} data-testid="v-url" className="vinp" placeholder="https://youtube.com/watch?v=… ou vimeo / mp4 / IG / TikTok" /></Fld>
          <Fld label="Discipline"><select value={f.discipline} onChange={(e) => set("discipline", e.target.value)} data-testid="v-discipline" className="vinp"><option value="">—</option>{refs?.disciplines.map((d) => <option key={d}>{d}</option>)}</select></Fld>
          <Fld label="Volet"><select value={f.volet} onChange={(e) => set("volet", e.target.value)} data-testid="v-volet" className="vinp"><option value="technique">Technique</option><option value="combo">Combinaison (payant)</option><option value="training">Training (bloc « peser sur play »)</option></select></Fld>
          <Fld label="Niveau / ceinture"><input value={f.belt_level} onChange={(e) => set("belt_level", e.target.value)} data-testid="v-belt" className="vinp" /></Fld>
          <Fld label="Crédits (gamification)"><input type="number" value={f.credits_reward} onChange={(e) => set("credits_reward", e.target.value)} data-testid="v-credits" className="vinp" /></Fld>
          <Fld label="Badge décerné (option)"><select value={f.badge} onChange={(e) => set("badge", e.target.value)} data-testid="v-badge" className="vinp"><option value="">Aucun</option>{refs?.badges.map((b) => <option key={b}>{b}</option>)}</select></Fld>
          <Fld label="Durée (min)"><input type="number" value={f.duration_min} onChange={(e) => set("duration_min", e.target.value)} data-testid="v-duration" className="vinp" /></Fld>
          <Fld label="Ordre"><input type="number" value={f.order} onChange={(e) => set("order", e.target.value)} data-testid="v-order" className="vinp" /></Fld>
          <Fld label="Vignette (URL, option)" full><input value={f.thumbnail_url} onChange={(e) => set("thumbnail_url", e.target.value)} data-testid="v-thumb" className="vinp" /></Fld>
          <Fld label="Tags (séparés par ,)" full><input value={f.tags} onChange={(e) => set("tags", e.target.value)} data-testid="v-tags" className="vinp" /></Fld>
          <Fld label="Description" full><textarea value={f.description} onChange={(e) => set("description", e.target.value)} rows={2} data-testid="v-desc" className="vinp" /></Fld>
        </div>
        <label className="flex items-center gap-2 text-sm mt-3"><input type="checkbox" checked={f.published} onChange={(e) => set("published", e.target.checked)} data-testid="v-published" className="w-4 h-4 accent-red-600" /> Publié (visible)</label>
        <div className="flex gap-2 mt-5">
          <button onClick={save} disabled={busy} data-testid="v-save" className="flex-1 text-white py-2.5 rounded font-semibold disabled:opacity-50" style={{ background: RED }}>{busy ? "…" : "Enregistrer"}</button>
          <button onClick={onClose} className="px-5 py-2.5 rounded border border-zinc-700 font-semibold">Annuler</button>
        </div>
        <style>{`.vinp{margin-top:.35rem;width:100%;background:#09090b;border:1px solid #3f3f46;border-radius:.375rem;padding:.5rem .75rem;font-size:.875rem;color:#fff;}`}</style>
      </div>
    </div>
  );
}

function BunnyUploader({ cfg, refs, onDone }) {
  const [jobs, setJobs] = useState([]);
  const [batchDiscipline, setBatchDiscipline] = useState("");
  const [batchAccess, setBatchAccess] = useState("membres");
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);
  const jobsRef = useRef(jobs);
  jobsRef.current = jobs;

  const upd = (id, patch) => setJobs((js) => js.map((j) => (j.id === id ? { ...j, ...patch } : j)));

  async function pollStatus(videoId, jobId, tries = 0) {
    try {
      const r = await api.get(`/videos/bunny/status/${videoId}`);
      const d = r.data;
      if (d.error) { upd(jobId, { status: "error", label: "Erreur d'encodage" }); return; }
      if (d.ready) {
        upd(jobId, { status: "done", label: "Prête ✓", progress: 100 });
        toast.success(`« ${jobsRef.current.find((j) => j.id === jobId)?.name || ""} » est prête`);
        onDone && onDone();
        return;
      }
      upd(jobId, { status: "processing", label: `Encodage… ${d.progress || 0}%` });
    } catch { /* ignore transient */ }
    if (tries < 150) setTimeout(() => pollStatus(videoId, jobId, tries + 1), 5000);
  }

  async function startUpload(file) {
    const jobId = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    setJobs((js) => [...js, { id: jobId, name: file.name, progress: 0, status: "init", label: "Préparation…" }]);
    let init;
    try {
      init = await api.post("/videos/bunny/init-upload", {
        title: file.name.replace(/\.[^.]+$/, ""), discipline: batchDiscipline, access_level: batchAccess,
      });
    } catch (e) {
      upd(jobId, { status: "error", label: e?.response?.data?.detail || "Init échouée" });
      return;
    }
    const { video_id, library_id, tus: t } = init.data;
    const upload = new tus.Upload(file, {
      endpoint: t.endpoint,
      retryDelays: [0, 3000, 5000, 10000, 20000, 60000],
      headers: {
        AuthorizationSignature: t.signature,
        AuthorizationExpire: String(t.expires),
        VideoId: video_id,
        LibraryId: String(library_id),
      },
      metadata: { filetype: file.type, title: file.name },
      onProgress(loaded, total) {
        upd(jobId, { status: "uploading", progress: Math.round((loaded / total) * 100), label: `Téléversement… ${Math.round((loaded / total) * 100)}%` });
      },
      onSuccess() {
        upd(jobId, { status: "processing", progress: 100, label: "Encodage…" });
        pollStatus(video_id, jobId);
      },
      onError(err) {
        upd(jobId, { status: "error", label: `Échec : ${String(err).slice(0, 60)}` });
      },
    });
    upload.findPreviousUploads().then((prev) => { if (prev.length) upload.resumeFromPreviousUpload(prev[0]); upload.start(); });
  }

  function handleFiles(fileList) {
    const files = Array.from(fileList || []).filter((f) => f.type.startsWith("video/") || /\.(mp4|mov|m4v|avi|mkv|webm)$/i.test(f.name));
    if (!files.length) { toast.error("Choisis des fichiers vidéo."); return; }
    files.forEach(startUpload);
  }

  if (cfg && !cfg.configured) {
    return (
      <div className="border border-amber-900/50 bg-amber-950/30 rounded-lg p-4 mb-6 flex items-start gap-3" data-testid="bunny-not-configured">
        <WarningCircle size={22} className="text-amber-400 shrink-0 mt-0.5" />
        <div>
          <h3 className="font-semibold text-amber-200">Bunny Stream pas encore branché</h3>
          <p className="text-sm text-amber-100/70 mt-0.5">La clé API Bunny doit être ajoutée par l'administrateur avant de pouvoir téléverser des vidéos.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="border border-zinc-800 bg-zinc-900/60 rounded-lg p-4 mb-6" data-testid="bunny-uploader">
      <div className="flex items-center gap-2 text-zinc-200 font-semibold mb-3"><CloudArrowUp size={20} weight="fill" className="text-emerald-400" /> Téléverser des vidéos (Bunny Stream)</div>
      <div className="grid sm:grid-cols-2 gap-3 mb-3">
        <label className="block"><span className="text-xs text-zinc-500">Discipline (appliquée au lot)</span>
          <select value={batchDiscipline} onChange={(e) => setBatchDiscipline(e.target.value)} data-testid="bunny-batch-discipline" className="mt-1 w-full bg-zinc-950 border border-zinc-700 rounded px-3 py-2 text-sm text-white">
            <option value="">—</option>{(refs?.disciplines || []).map((d) => <option key={d}>{d}</option>)}
          </select>
        </label>
        <label className="block"><span className="text-xs text-zinc-500">Niveau d'accès (appliqué au lot)</span>
          <select value={batchAccess} onChange={(e) => setBatchAccess(e.target.value)} data-testid="bunny-batch-access" className="mt-1 w-full bg-zinc-950 border border-zinc-700 rounded px-3 py-2 text-sm text-white">
            {(refs?.access_levels || ["public", "membres", "premium"]).map((a) => <option key={a}>{a}</option>)}
          </select>
        </label>
      </div>
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files); }}
        onClick={() => inputRef.current?.click()}
        data-testid="bunny-dropzone"
        className={`cursor-pointer border-2 border-dashed rounded-lg p-8 text-center transition-colors ${dragOver ? "border-emerald-500 bg-emerald-950/20" : "border-zinc-700 hover:border-zinc-500"}`}>
        <CloudArrowUp size={34} className="mx-auto text-zinc-500 mb-2" />
        <p className="text-sm text-zinc-300">Glisse-dépose tes vidéos ici, ou <span className="text-emerald-400 underline">clique pour choisir</span></p>
        <p className="text-xs text-zinc-600 mt-1">MP4, MOV, MKV… Téléversement direct et résumable. Tu peux en envoyer plusieurs à la fois.</p>
        <input ref={inputRef} type="file" accept="video/*" multiple className="hidden" data-testid="bunny-file-input" onChange={(e) => handleFiles(e.target.files)} />
      </div>

      {jobs.length > 0 && (
        <div className="mt-4 space-y-2" data-testid="bunny-jobs">
          {jobs.map((j) => (
            <div key={j.id} className="bg-zinc-950 border border-zinc-800 rounded p-2.5" data-testid={`bunny-job-${j.id}`}>
              <div className="flex items-center gap-2 text-sm">
                {j.status === "done" ? <CheckCircle size={16} weight="fill" className="text-emerald-400" />
                  : j.status === "error" ? <WarningCircle size={16} weight="fill" className="text-red-500" />
                    : <CircleNotch size={16} className="animate-spin text-zinc-400" />}
                <span className="flex-1 truncate text-zinc-200">{j.name}</span>
                <span className={`text-xs ${j.status === "error" ? "text-red-400" : j.status === "done" ? "text-emerald-400" : "text-zinc-400"}`}>{j.label}</span>
              </div>
              {(j.status === "uploading" || j.status === "processing") && (
                <div className="mt-1.5 h-1.5 bg-zinc-800 rounded overflow-hidden">
                  <div className={`h-full ${j.status === "processing" ? "bg-amber-500 animate-pulse w-full" : "bg-emerald-500"}`} style={{ width: `${j.progress}%` }} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const _STOP = new Set(["le", "la", "les", "un", "une", "de", "du", "des", "et", "a", "au", "aux", "en", "sur", "pour", "avec", "dans", "par", "ma", "mon", "mes", "ton", "ta", "the", "apprenez", "apprendre", "technique", "simple", "video", "cours", "tuto", "tutoriel", "comment", "kaizen", "system", "systeme"]);
function _tokens(s) {
  return (s || "").toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s]/g, " ")
    .split(/\s+/).filter((w) => w.length >= 3 && !_STOP.has(w));
}
function _matchScore(videoTokens, name) {
  const nt = new Set(_tokens(name));
  if (!nt.size) return 0;
  let hits = 0;
  for (const w of videoTokens) if (nt.has(w)) hits += 1;
  return hits;
}

const ACADEMY_DISCIPLINES = [
  { code: "muay_thai_kickboxing", name: "Kickboxing / Muay Thaï" },
  { code: "jujutsu_japonais", name: "Jiu-jitsu Can Ryu" },
  { code: "bjj", name: "Jiu-jitsu Brésilien (BJJ)" },
];

function AttachTechniqueModal({ video, onClose }) {
  const [disc, setDisc] = useState("");
  const [techs, setTechs] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [bulkSaving, setBulkSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const embedUrl = video.embed_url || video.video_url;
  const vTokens = _tokens(video.title);

  useEffect(() => {
    if (!disc) { setTechs([]); return; }
    setLoading(true);
    setSelected(new Set());
    api.get(`/academy/admin/curriculum?discipline=${disc}`)
      .then((r) => {
        const list = r.data.techniques || [];
        setTechs(list);
        // Rattacher par titre : pré-sélectionne le meilleur candidat si la correspondance est forte
        let best = null, bestScore = 1;
        for (const t of list) {
          if (t.video_url === embedUrl) continue;
          const s = _matchScore(vTokens, t.name);
          if (s > bestScore) { best = t; bestScore = s; }
        }
        if (best) setSelected(new Set([best.id]));
      })
      .catch(() => toast.error("Erreur chargement techniques"))
      .finally(() => setLoading(false));
  }, [disc]);

  async function attach(t) {
    setSavingId(t.id);
    try {
      await api.put(`/academy/admin/curriculum/${t.id}/video`, { video_url: embedUrl });
      setTechs((ts) => ts.map((x) => (x.id === t.id ? { ...x, video_url: embedUrl } : x)));
      toast.success(`« ${video.title} » rattachée à « ${t.name} »`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    setSavingId(null);
  }

  const filtered = techs
    .filter((t) => !q || (t.name || "").toLowerCase().includes(q.toLowerCase()))
    .map((t) => ({ ...t, _score: _matchScore(vTokens, t.name) }))
    .sort((a, b) => b._score - a._score);
  const isThis = (t) => t.video_url && t.video_url === embedUrl;

  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => {
    const ids = filtered.filter((t) => !isThis(t)).map((t) => t.id);
    setSelected((s) => (ids.every((id) => s.has(id)) ? new Set() : new Set(ids)));
  };

  async function bulkAttach() {
    const ids = [...selected];
    if (!ids.length) return;
    setBulkSaving(true);
    try {
      const items = ids.map((id) => ({ technique_id: id, video_url: embedUrl }));
      const r = await api.post("/academy/admin/curriculum/videos/bulk", { items });
      setTechs((ts) => ts.map((x) => (selected.has(x.id) ? { ...x, video_url: embedUrl } : x)));
      setSelected(new Set());
      toast.success(`Rattachée à ${r.data.updated} technique(s)`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    setBulkSaving(false);
  }

  return (
    <div className="fixed inset-0 z-[80] bg-black/70 flex items-center justify-center p-4" onClick={onClose} data-testid="attach-modal">
      <div className="bg-zinc-900 border border-zinc-700 rounded-xl w-full max-w-lg max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b border-zinc-800">
          <div className="flex items-center gap-2 text-white font-bold"><LinkSimple size={18} className="text-cyan-400" /> Rattacher à une ou plusieurs techniques</div>
          <div className="flex items-center justify-between gap-2 mt-1">
            <p className="text-xs text-zinc-500 truncate flex-1">Vidéo : <span className="text-zinc-300">{video.title}</span></p>
            <button onClick={() => setShowPreview((v) => !v)} data-testid="attach-preview-toggle" className="text-xs px-2.5 py-1 rounded border border-emerald-700 text-emerald-300 hover:bg-emerald-950/40 flex items-center gap-1 shrink-0"><Play size={13} weight="fill" /> {showPreview ? "Masquer" : "Voir la vidéo"}</button>
          </div>
          {showPreview && (
            _isFileVideo(_previewSrc(embedUrl))
              ? <video src={_previewSrc(embedUrl)} controls autoPlay className="w-full rounded-lg bg-black aspect-video mt-3" data-testid="attach-preview-player" />
              : _previewSrc(embedUrl)
                ? <div className="relative w-full rounded-lg overflow-hidden bg-black mt-3" style={{ paddingTop: "56.25%" }}>
                    <iframe src={_previewSrc(embedUrl)} title={video.title} allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture" allowFullScreen loading="lazy" className="absolute inset-0 w-full h-full" data-testid="attach-preview-player" />
                  </div>
                : <div className="text-xs text-zinc-500 py-4 text-center mt-3" data-testid="attach-preview-empty">Aucune vidéo à afficher.</div>
          )}
        </div>
        <div className="p-4 space-y-3 overflow-y-auto flex-1">
          <select value={disc} onChange={(e) => setDisc(e.target.value)} data-testid="attach-discipline" className="w-full bg-zinc-950 border border-zinc-700 rounded px-3 py-2 text-sm text-white">
            <option value="">Choisir une discipline…</option>
            {ACADEMY_DISCIPLINES.map((d) => <option key={d.code} value={d.code}>{d.name}</option>)}
          </select>
          {disc && (
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <MagnifyingGlass size={15} className="absolute left-2.5 top-2.5 text-zinc-500" />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher une technique…" data-testid="attach-search" className="w-full bg-zinc-950 border border-zinc-700 rounded pl-8 pr-3 py-2 text-sm text-white" />
              </div>
              {filtered.length > 0 && <button onClick={toggleAll} data-testid="attach-toggle-all" className="text-xs text-cyan-400 hover:text-cyan-300 whitespace-nowrap px-2">Tout sélectionner</button>}
            </div>
          )}
          {loading && <p className="text-zinc-500 text-sm text-center py-4"><CircleNotch size={18} className="animate-spin inline" /> Chargement…</p>}
          {!loading && disc && filtered.map((t) => (
            <div key={t.id} className="flex items-center gap-2 bg-zinc-950 border border-zinc-800 rounded px-3 py-2" data-testid={`attach-tech-${t.id}`}>
              {!isThis(t) && (
                <input type="checkbox" checked={selected.has(t.id)} onChange={() => toggle(t.id)} data-testid={`attach-check-${t.id}`} className="accent-cyan-500 w-4 h-4" />
              )}
              <div className="flex-1 min-w-0">
                <div className="text-sm text-zinc-200 truncate flex items-center gap-1.5">
                  {t.name}
                  {t._score >= 2 && !isThis(t) && <span className="text-[9px] uppercase tracking-wide bg-cyan-500/15 text-cyan-300 border border-cyan-500/30 px-1.5 py-0.5 rounded" data-testid={`attach-suggested-${t.id}`}>✨ suggéré</span>}
                </div>
                <div className="text-[10px] text-zinc-500">{t.belt || "—"}{t.video_url && !isThis(t) ? " · a déjà une vidéo" : ""}</div>
              </div>
              {isThis(t)
                ? <span className="text-[11px] text-emerald-400 flex items-center gap-1"><CheckCircle size={14} weight="fill" /> rattachée</span>
                : <button onClick={() => attach(t)} disabled={savingId === t.id} data-testid={`attach-btn-${t.id}`} className="text-xs bg-cyan-600 hover:bg-cyan-500 disabled:opacity-50 text-white px-3 py-1.5 rounded font-semibold">{savingId === t.id ? "…" : "Rattacher"}</button>}
            </div>
          ))}
          {!loading && disc && filtered.length === 0 && <p className="text-zinc-500 text-sm text-center py-4">Aucune technique.</p>}
        </div>
        <div className="p-3 border-t border-zinc-800 flex items-center justify-between gap-2">
          <button onClick={onClose} data-testid="attach-close" className="text-sm text-zinc-400 hover:text-white px-4 py-2">Fermer</button>
          {selected.size > 0 && (
            <button onClick={bulkAttach} disabled={bulkSaving} data-testid="attach-bulk-btn" className="text-sm bg-cyan-600 hover:bg-cyan-500 disabled:opacity-50 text-white px-4 py-2 rounded-lg font-semibold">
              {bulkSaving ? "Rattachement…" : `Rattacher aux ${selected.size} sélectionnées`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function ImportProgress({ count }) {
  const [target, setTarget] = useState(() => parseInt(localStorage.getItem("bunny_import_target") || "87", 10) || 87);
  const [editing, setEditing] = useState(false);
  const pct = target > 0 ? Math.min(100, Math.round((count / target) * 100)) : 0;
  const done = count >= target;
  const save = (v) => { const n = Math.max(1, parseInt(v, 10) || 1); setTarget(n); localStorage.setItem("bunny_import_target", String(n)); };
  return (
    <div className="border border-zinc-800 bg-zinc-900/60 rounded-lg p-4 mb-6" data-testid="import-progress">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div className="flex items-center gap-2 text-sm font-semibold text-zinc-200">
          <CloudArrowUp size={18} weight="fill" className={done ? "text-emerald-400" : "text-cyan-400"} />
          Progression du téléversement Bunny
        </div>
        <div className="text-sm text-zinc-400 flex items-center gap-1.5">
          <span className={`font-bold ${done ? "text-emerald-400" : "text-cyan-300"}`} data-testid="import-count">{count}</span> /
          {editing ? (
            <input type="number" defaultValue={target} autoFocus onBlur={(e) => { save(e.target.value); setEditing(false); }}
              onKeyDown={(e) => { if (e.key === "Enter") { save(e.target.value); setEditing(false); } }}
              data-testid="import-target-input" className="w-16 bg-zinc-950 border border-zinc-700 rounded px-1.5 py-0.5 text-sm text-white" />
          ) : (
            <button onClick={() => setEditing(true)} data-testid="import-target-btn" className="font-bold text-zinc-200 hover:text-white underline decoration-dotted">{target}</button>
          )}
          <span>vidéos importées</span>
        </div>
      </div>
      <div className="h-2.5 bg-zinc-800 rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all duration-500 ${done ? "bg-emerald-500" : "bg-gradient-to-r from-cyan-500 to-blue-500"}`} style={{ width: `${pct}%` }} data-testid="import-bar" />
      </div>
      <p className="text-[11px] text-zinc-500 mt-1.5">{done ? "🎉 Objectif atteint — toutes tes vidéos sont sur Bunny !" : `Encore ${Math.max(0, target - count)} vidéo(s) à importer (${pct}%). Clique sur le nombre pour ajuster l'objectif.`}</p>
    </div>
  );
}

function KPI({ label, count, icon: Icon, color, testid }) {
  const c = { emerald: "text-emerald-400 border-emerald-900", blue: "text-blue-400 border-blue-900", amber: "text-amber-400 border-amber-900" }[color];
  return (
    <div className={`bg-zinc-900 border rounded-lg p-4 ${c}`} data-testid={testid}>
      <div className="flex items-center gap-2 text-zinc-500 text-xs uppercase tracking-wider mb-1"><Icon size={14} /> {label}</div>
      <div className="text-3xl font-bold text-zinc-100">{count}</div>
    </div>
  );
}

function Fld({ label, children, full }) {
  return <label className={`block ${full ? "col-span-2" : ""}`}><span className="text-xs text-zinc-500">{label}</span>{children}</label>;
}

function MRRStat({ label, value, accent, testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-[11px] uppercase tracking-wider text-zinc-500">{label}</div>
      <div className={`text-2xl font-bold ${accent ? "text-amber-300" : "text-zinc-100"}`}>{value}</div>
    </div>
  );
}

// ─── IA : jumelage des vidéos aux techniques du carnet (suggère → confirme) ──
function AiMatchModal({ onClose }) {
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [checked, setChecked] = useState({});
  const [ran, setRan] = useState(false);
  const [info, setInfo] = useState("");
  const [preview, setPreview] = useState(null);

  const run = async () => {
    setLoading(true); setRan(false);
    try {
      const { data } = await api.post("/curriculum/ai-match-videos", {});
      const sugg = data.suggestions || [];
      setSuggestions(sugg);
      const ck = {}; sugg.forEach((s, i) => { ck[i] = (s.confiance || 0) >= 70; });
      setChecked(ck);
      setInfo(data.message || `${sugg.length} proposition(s) · ${data.techniques_sans_video || 0} technique(s) sans vidéo`);
      setRan(true);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur IA"); }
    finally { setLoading(false); }
  };

  const apply = async () => {
    const matches = suggestions.filter((_, i) => checked[i]).map((s) => ({ technique_id: s.technique_id, video_url: s.video_url }));
    if (matches.length === 0) { toast.error("Coche au moins une proposition."); return; }
    setApplying(true);
    try { const { data } = await api.post("/curriculum/ai-apply-matches", { matches }); toast.success(`${data.applied} vidéo(s) reliée(s) aux carnets`); onClose(); }
    catch (e) { toast.error("Erreur d'application"); }
    finally { setApplying(false); }
  };

  const runAuto = async () => {
    setApplying(true); setRan(false);
    try {
      const { data } = await api.post("/curriculum/ai-match-videos/auto/start", {});
      const jobId = data.job_id;
      setInfo("Jumelage en cours… (traitement par discipline)"); setRan(true);
      const started = Date.now();
      const poll = async () => {
        try {
          const { data: job } = await api.get(`/curriculum/ai-match-videos/job/${jobId}`);
          if (job.status === "done") {
            toast.success(`${job.applied} vidéo(s) reliée(s) automatiquement aux carnets (sur ${job.proposees || 0} proposition(s))`);
            onClose(); return;
          }
          if (job.status === "error") { toast.error("Erreur du jumelage automatique"); setApplying(false); return; }
          if (job.total_disc) setInfo(`Jumelage en cours… ${job.done_disc}/${job.total_disc} discipline(s)`);
          if (Date.now() - started > 300000) { toast.error("Le jumelage prend trop de temps. Réessaie."); setApplying(false); return; }
          setTimeout(poll, 3000);
        } catch { setTimeout(poll, 3000); }
      };
      setTimeout(poll, 2500);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur du jumelage automatique"); setApplying(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-zinc-950 border border-zinc-800 rounded-xl max-w-2xl w-full my-8 text-zinc-100" onClick={(e) => e.stopPropagation()} data-testid="ai-match-modal">
        <div className="border-b border-zinc-800 px-6 py-4 flex items-center justify-between">
          <div className="font-bold text-lg">🤖 Jumeler les vidéos aux techniques (IA)</div>
          <button onClick={onClose} className="text-zinc-400 hover:text-white">✕</button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-zinc-400">L'IA propose la bonne vidéo pour chaque technique du carnet (bonne ceinture, toutes disciplines). Tu confirmes les propositions, OU tu jumelles tout en un clic.</p>
          <div className="flex flex-wrap gap-2">
            <button onClick={run} disabled={loading || applying} data-testid="ai-match-run" className="px-5 py-2.5 rounded-md bg-emerald-600 hover:bg-emerald-500 font-semibold disabled:opacity-50">{loading ? "Analyse en cours…" : "🔍 Proposer (à confirmer)"}</button>
            <button onClick={runAuto} disabled={loading || applying} data-testid="ai-match-auto" className="px-5 py-2.5 rounded-md bg-amber-500 hover:bg-amber-400 text-black font-semibold disabled:opacity-50">{applying ? "Jumelage…" : "⚡ Tout jumeler (1 clic, toutes disciplines)"}</button>
          </div>
          {ran && <div className="text-xs text-zinc-500">{info}</div>}

          <div className="space-y-2 max-h-[45vh] overflow-auto">
            {suggestions.map((s, i) => (
              <label key={i} data-testid={`ai-match-item-${i}`} className="flex items-start gap-3 p-3 rounded-lg border border-zinc-800 hover:border-zinc-600 cursor-pointer">
                <input type="checkbox" checked={!!checked[i]} onChange={(e) => setChecked({ ...checked, [i]: e.target.checked })} className="mt-1" data-testid={`ai-match-check-${i}`} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate">{s.technique_name} <span className="text-zinc-500">←</span> {s.video_title}</div>
                  <div className="text-xs text-zinc-500 mt-0.5">Ceinture technique : {s.technique_belt || "—"} · vidéo : {s.video_belt || "—"} {s.belt_match ? <span className="text-emerald-400">✓ même ceinture</span> : <span className="text-amber-400">⚠ ceinture différente</span>}</div>
                  {s.raison && <div className="text-xs text-zinc-600 mt-0.5">{s.raison}</div>}
                </div>
                <button type="button" onClick={(e) => { e.preventDefault(); setPreview({ url: s.video_url, title: s.video_title }); }} data-testid={`ai-match-preview-${i}`} title="Aperçu de la vidéo proposée" className="text-emerald-400 hover:text-emerald-300 shrink-0"><Play size={16} weight="fill" /></button>
                <span className={`text-xs font-bold ${(s.confiance || 0) >= 70 ? "text-emerald-400" : "text-amber-400"}`}>{s.confiance}%</span>
              </label>
            ))}
            {ran && suggestions.length === 0 && <div className="text-sm text-zinc-500 text-center py-6">Aucune proposition (toutes les techniques ont déjà une vidéo, ou aucune correspondance fiable).</div>}
          </div>

          {suggestions.length > 0 && (
            <button onClick={apply} disabled={applying} data-testid="ai-match-apply" className="w-full px-5 py-2.5 rounded-md bg-amber-500 hover:bg-amber-400 text-black font-semibold disabled:opacity-50">{applying ? "Application…" : `✅ Appliquer les propositions cochées (${Object.values(checked).filter(Boolean).length})`}</button>
          )}
        </div>
      </div>
      {preview && <VideoPreviewModal url={preview.url} title={preview.title} onClose={() => setPreview(null)} />}
    </div>
  );
}


// ─── Liste de tournage : techniques sans vidéo par discipline + ceinture ────
function ShootingListModal({ onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { (async () => {
    try { const { data } = await api.get("/curriculum/shooting-list"); setData(data); }
    catch { toast.error("Erreur de chargement de la liste"); }
    finally { setLoading(false); }
  })(); }, []);

  const [filmedIds, setFilmedIds] = useState({});
  useEffect(() => {
    if (!data) return;
    const init = {};
    Object.values(data.groups).forEach((belts) => Object.values(belts).forEach((items) => items.forEach((it) => { init[it.id] = it.filmed; })));
    setFilmedIds(init);
  }, [data]);

  const toggleFilmed = async (id) => {
    const next = !filmedIds[id];
    setFilmedIds((p) => ({ ...p, [id]: next }));
    try { await api.post(`/curriculum/technique/${id}/tournage`, { status: next ? "filme" : "a_filmer" }); }
    catch { toast.error("Erreur"); setFilmedIds((p) => ({ ...p, [id]: !next })); }
  };
  const filmedTotal = Object.values(filmedIds).filter(Boolean).length;

  const dl = async (fmt) => {
    try {
      const resp = await api.get(`/curriculum/shooting-list?format=${fmt}`, { responseType: "blob" });
      const type = fmt === "pdf" ? "application/pdf" : "text/csv";
      const url = URL.createObjectURL(new Blob([resp.data], { type }));
      if (fmt === "pdf") { window.open(url, "_blank"); }
      else { const a = document.createElement("a"); a.href = url; a.download = "liste_tournage.csv"; document.body.appendChild(a); a.click(); a.remove(); }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error("Erreur d'export"); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-zinc-950 border border-zinc-800 rounded-lg max-w-2xl w-full my-8" onClick={(e) => e.stopPropagation()} data-testid="shooting-list-modal">
        <div className="border-b border-zinc-800 px-6 py-4 flex items-center justify-between">
          <div className="font-bold text-lg text-zinc-100">🎥 Liste de tournage {data ? <span className="text-zinc-500 text-sm font-normal">({data.count} sans vidéo · <span className="text-emerald-400" data-testid="filmed-count">{filmedTotal}</span> filmée(s))</span> : null}</div>
          <button onClick={onClose} className="text-zinc-400 hover:text-white text-xl">×</button>
        </div>
        <div className="px-6 py-3 flex gap-2 border-b border-zinc-800 items-center">
          <button onClick={() => dl("pdf")} data-testid="shooting-pdf-btn" className="text-sm px-3 py-1.5 rounded border border-zinc-700 text-zinc-200 hover:bg-white/5 font-semibold">📄 PDF</button>
          <button onClick={() => dl("csv")} data-testid="shooting-csv-btn" className="text-sm px-3 py-1.5 rounded bg-emerald-600 hover:bg-emerald-500 text-white font-semibold">📊 CSV (Excel)</button>
          <span className="text-xs text-zinc-500 ml-2">Coche une technique une fois filmée ✓</span>
        </div>
        <div className="p-6 max-h-[60vh] overflow-y-auto space-y-5">
          {loading && <div className="text-center py-8"><CircleNotch size={28} className="animate-spin text-zinc-500 mx-auto" /></div>}
          {data && data.count === 0 && <div className="text-center py-8 text-emerald-400">🎉 Toutes les techniques ont une vidéo !</div>}
          {data && Object.keys(data.groups).sort().map((disc) => (
            <div key={disc} data-testid={`shooting-disc-${disc}`}>
              <div className="text-sm font-bold text-red-400 uppercase tracking-wider mb-2">{disc}</div>
              {Object.keys(data.groups[disc]).map((belt) => (
                <div key={belt} className="mb-2">
                  <div className="text-xs font-semibold text-zinc-400 mb-1">Ceinture {belt} <span className="text-zinc-600">({data.groups[disc][belt].length})</span></div>
                  <div className="flex flex-wrap gap-1.5">
                    {data.groups[disc][belt].map((it) => {
                      const done = filmedIds[it.id];
                      return (
                        <button key={it.id} onClick={() => toggleFilmed(it.id)} data-testid={`shooting-item-${it.id}`}
                          className={`text-xs px-2 py-1 rounded border transition-colors ${done ? "bg-emerald-600/25 border-emerald-600 text-emerald-300 line-through" : "bg-zinc-900 border-zinc-800 text-zinc-300 hover:border-zinc-600"}`}>
                          {done ? "✓ " : ""}{it.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Liens manuels vidéo ↔ technique (verrouillage) ─────────────────────────
function ManualLinkModal({ onClose }) {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [disc, setDisc] = useState("");
  const [onlyNoVideo, setOnlyNoVideo] = useState(false);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (disc) params.set("discipline", disc);
      if (onlyNoVideo) params.set("only_no_video", "true");
      const { data } = await api.get(`/curriculum/manual-link/data?${params.toString()}`);
      setData(data);
    } catch { toast.error("Erreur de chargement"); }
    finally { setLoading(false); }
  }, [q, disc, onlyNoVideo]);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  const setVideo = async (tid, video_url) => {
    if (!video_url) return;
    try { await api.post(`/curriculum/technique/${tid}/set-video`, { video_url }); toast.success("Vidéo reliée et verrouillée 🔒"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  const unlink = async (tid) => {
    try { await api.post(`/curriculum/technique/${tid}/unlink-video`); toast.success("Vidéo retirée"); load(); }
    catch { toast.error("Erreur"); }
  };
  const toggleLock = async (tid) => {
    try { const r = await api.post(`/curriculum/technique/${tid}/toggle-lock`); toast.success(r.data.video_locked ? "Verrouillé 🔒" : "Déverrouillé (re-jumelage autorisé)"); load(); }
    catch { toast.error("Erreur"); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-zinc-950 border border-zinc-800 rounded-lg max-w-4xl w-full my-8" onClick={(e) => e.stopPropagation()} data-testid="manual-link-modal">
        <div className="border-b border-zinc-800 px-6 py-4 flex items-center justify-between">
          <div className="font-bold text-lg text-zinc-100">🔗 Liens manuels vidéo ↔ technique</div>
          <button onClick={onClose} className="text-zinc-400 hover:text-white text-xl">×</button>
        </div>
        <div className="px-6 py-3 flex flex-wrap gap-2 border-b border-zinc-800 items-center">
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="manual-search" placeholder="Rechercher une technique…" className="flex-1 min-w-[180px] bg-zinc-900 border border-zinc-800 rounded px-3 py-1.5 text-sm text-zinc-200" />
          <select value={disc} onChange={(e) => setDisc(e.target.value)} data-testid="manual-disc-filter" className="bg-zinc-900 border border-zinc-800 rounded px-2 py-1.5 text-sm text-zinc-200">
            <option value="">Toutes disciplines</option>
            {(data?.disciplines || []).map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
          <label className="text-xs text-zinc-400 flex items-center gap-1"><input type="checkbox" checked={onlyNoVideo} onChange={(e) => setOnlyNoVideo(e.target.checked)} data-testid="manual-only-novideo" /> Sans vidéo</label>
        </div>
        <div className="p-4 max-h-[62vh] overflow-y-auto">
          {loading && <div className="text-center py-8"><CircleNotch size={28} className="animate-spin text-zinc-500 mx-auto" /></div>}
          {data && data.techniques.length === 0 && <div className="text-center py-8 text-zinc-500">Aucune technique.</div>}
          {data && data.techniques.map((t) => (
            <div key={t.id} data-testid={`manual-row-${t.id}`} className="flex flex-wrap items-center gap-2 py-2 border-b border-zinc-900">
              <div className="flex-1 min-w-[200px]">
                <div className="text-sm text-zinc-100">{t.video_locked && "🔒 "}{t.name}</div>
                <div className="text-[11px] text-zinc-500">{t.discipline} · {t.belt} {t.video_title ? <span className="text-emerald-400">· 🎬 {t.video_title}</span> : <span className="text-amber-500">· sans vidéo</span>} {t.video_source === "manual" && <span className="text-sky-400">· manuel</span>}</div>
              </div>
              <select data-testid={`manual-video-select-${t.id}`} defaultValue="" onChange={(e) => { setVideo(t.id, e.target.value); e.target.value = ""; }} className="bg-zinc-900 border border-zinc-800 rounded px-2 py-1 text-xs text-zinc-200 max-w-[220px]">
                <option value="">— Relier une vidéo —</option>
                {(data.videos || []).map((v) => <option key={v.id} value={v.video_url}>{v.title} {v.belt_level ? `(${v.belt_level})` : ""}</option>)}
              </select>
              {!t.video_url && t.suggested_video_url && (
                <button onClick={() => setVideo(t.id, t.suggested_video_url)} data-testid={`manual-suggest-${t.id}`} title={t.suggested_video_title}
                  className="text-xs px-2 py-1 rounded border border-amber-600 text-amber-300 hover:bg-amber-950/40 max-w-[220px] truncate">⭐ {t.suggested_video_title}</button>
              )}
              {t.video_url && <button onClick={() => setPreview({ url: t.video_url, title: t.video_title || t.name })} data-testid={`manual-preview-${t.id}`} className="text-xs px-2 py-1 rounded border border-emerald-800 text-emerald-300 hover:bg-emerald-950/40 flex items-center gap-1"><Play size={12} weight="fill" /> Aperçu</button>}
              {t.video_url && <button onClick={() => toggleLock(t.id)} data-testid={`manual-lock-${t.id}`} className="text-xs px-2 py-1 rounded border border-zinc-700 text-zinc-300 hover:bg-white/5">{t.video_locked ? "🔓 Déverrouiller" : "🔒 Verrouiller"}</button>}
              {t.video_url && <button onClick={() => unlink(t.id)} data-testid={`manual-unlink-${t.id}`} className="text-xs px-2 py-1 rounded border border-red-800 text-red-300 hover:bg-red-950/40">Retirer</button>}
            </div>
          ))}
        </div>
      </div>
      {preview && <VideoPreviewModal url={preview.url} title={preview.title} onClose={() => setPreview(null)} />}
    </div>
  );
}



// ─── Tableau de bord tournage : couverture par discipline + ceinture ────────
function TournageDashboardModal({ coverage, onClose }) {
  const byDB = coverage?.by_discipline_belt || {};
  // Priorités : ceintures avec la plus faible couverture d'abord
  const priorities = [];
  Object.entries(byDB).forEach(([disc, belts]) => {
    Object.entries(belts).forEach(([belt, s]) => {
      const missing = (s.total || 0) - (s.with_video || 0);
      if (missing > 0) priorities.push({ disc, belt, missing, total: s.total, with_video: s.with_video });
    });
  });
  priorities.sort((a, b) => b.missing - a.missing);

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-zinc-950 border border-zinc-800 rounded-lg max-w-2xl w-full my-8" onClick={(e) => e.stopPropagation()} data-testid="tournage-dash-modal">
        <div className="border-b border-zinc-800 px-6 py-4 flex items-center justify-between">
          <div className="font-bold text-lg text-zinc-100">📊 Tableau de bord tournage</div>
          <button onClick={onClose} className="text-zinc-400 hover:text-white text-xl">×</button>
        </div>
        <div className="px-6 py-3 border-b border-zinc-800 text-sm text-zinc-400 flex flex-wrap gap-4">
          <span>Couverture globale : <span className="text-emerald-400 font-bold">{coverage?.with_video || 0}</span> / {coverage?.total || 0} ({coverage?.pct || 0}%)</span>
          <span>🔒 {coverage?.manual_count || 0} manuel(s)</span>
          <span>🤖 {coverage?.ai_count || 0} par IA</span>
        </div>
        <div className="p-6 max-h-[62vh] overflow-y-auto space-y-5">
          {/* Couverture par discipline → ceinture */}
          {Object.keys(byDB).sort().map((disc) => (
            <div key={disc} data-testid={`dash-disc-${disc}`}>
              <div className="text-sm font-bold text-red-400 uppercase tracking-wider mb-2">{disc}</div>
              <div className="space-y-1.5">
                {Object.entries(byDB[disc]).map(([belt, s]) => {
                  const pct = s.total ? Math.round((s.with_video / s.total) * 100) : 0;
                  return (
                    <div key={belt} className="flex items-center gap-2 text-xs">
                      <span className="w-28 shrink-0 text-zinc-400 truncate">{belt}</span>
                      <div className="flex-1 h-2 rounded-full bg-zinc-800 overflow-hidden">
                        <div className="h-full bg-emerald-500" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="w-16 shrink-0 text-right text-zinc-500">{s.with_video}/{s.total}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
          {/* Priorités de tournage */}
          <div data-testid="dash-priorities">
            <div className="text-sm font-bold text-amber-400 uppercase tracking-wider mb-2">🎯 Priorités de tournage (plus gros manques)</div>
            {priorities.length === 0 && <div className="text-emerald-400 text-sm">🎉 Aucune ceinture incomplète !</div>}
            {priorities.slice(0, 12).map((p, i) => (
              <div key={i} className="flex items-center justify-between text-xs py-1 border-b border-zinc-900">
                <span className="text-zinc-300">{p.disc} · <span className="text-zinc-400">{p.belt}</span></span>
                <span className="text-amber-400 font-semibold">{p.missing} à filmer <span className="text-zinc-600">({p.with_video}/{p.total})</span></span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
